/**
 * StarterGuidePage — included Starter Guide Bundle sales page.
 *
 * Tripwire offer that the homepage RollingHero scroller links to.
 * Sells: 30-page Starter Guide PDF + 7 ScreenPal video scripts + 6 bonuses.
 *
 * Buy button calls POST /api/subscription/checkout with plan_id="starter_guide_17"
 * (one_time, ). On success Stripe redirects to /checkout/success which already
 * exists and handles all plan_ids.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import { Check, Star, Lock, ArrowRight, Sparkles, Video } from "lucide-react";

const NAVY = "#0F172A";
const GOLD = "#2E9F3A";
const RED = "#C9202E";
const ORANGE = "#F39A1F";
const CREAM = "#FFFFFF";

const VIDEOS = [
  { num: 1, title: "Welcome + The Math",        runtime: "~5 min", desc: "The Promise. The math behind  in 30 days." },
  { num: 2, title: "Week 1 — Set The Trap",     runtime: "~7 min", desc: "Build your Mirror Site, first funnel, 7-day email sequence." },
  { num: 3, title: "Week 2 — Fill The Trap",    runtime: "~5 min", desc: "The 5 free traffic places, daily." },
  { num: 4, title: "Week 3 — Convert The Trap", runtime: "~7 min", desc: "The 7-day email sequence does the selling." },
  { num: 5, title: "Week 4 — Rinse & Repeat",   runtime: "~5 min", desc: "Find the winner. 10x it. Cross ." },
  { num: 6, title: "The Upsell Ladder",         runtime: "~5 min", desc: "included → $49 → $79 → $1,073/year per buyer." },
  { num: 7, title: "Your First Day",            runtime: "~5 min", desc: "6 things to do today. Noah Principle close." },
];

const BONUSES = [
  { num: 1, title: "5 Headlines That Win",       desc: "Swipe file. Copy + paste, swap one word." },
  { num: 2, title: "3 Free Giveaway Ideas",      desc: "Pulled from The Market — no creating from scratch." },
  { num: 3, title: "My First Email",             desc: "Word-for-word template. Subject line included." },
  { num: 4, title: "60-Second YouTube Script",   desc: "One take. One camera. Your face. Done." },
  { num: 5, title: "Mike's Cheat Sheet",         desc: "Laminate it. Tape it to your monitor." },
  { num: 6, title: "30-Day Tear-Out Checklist",  desc: "One box per day. Refrigerator-ready." },
];

export default function StarterGuidePage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);

  const buy = async () => {
    if (!user) {
      // Send guests to signup with intent so they land here after auth
      nav("/signup?next=/starter-guide");
      return;
    }
    setLoading(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/subscription/checkout", {
        plan_id: "starter_guide_17",
        origin_url: origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Couldn't start checkout. Try again in a moment.");
        setLoading(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: CREAM }}>
      <SiteNav />

      {/* ───────────── HERO ───────────── */}
      <section className="relative overflow-hidden" style={{ background: "#FFFFFF" }}>
        <div className="absolute inset-0 opacity-10" style={{ background: "radial-gradient(ellipse at top right, #F2CB5C 0%, transparent 60%)" }} />
        <div className="relative max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] tracking-[0.28em] font-bold uppercase mb-5"
                   style={{ background: GOLD, color: NAVY }}>
                <Star className="w-3 h-3" /> Faitharama Starter Guide · Digital PDF
              </div>
              <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white uppercase leading-[1.02]">
                Your First <span style={{ color: GOLD }}></span><br/>
                In 30 Days.
              </h1>
              <div className="h-1 w-24 mt-5" style={{ background: GOLD }} />
              <p className="text-base md:text-lg mt-6 leading-relaxed" style={{ color: "#E6E2D2" }}>
                A 30-day blueprint for the man or woman ready to build something that lasts.
                <span className="block mt-2 text-[#F2CB5C]">No paid ads. No tech degree. No begging your family.</span>
                <span className="block mt-3 text-xs uppercase tracking-wider opacity-90">Note: This is the digital PDF guide only. The paperback book is in production — coming soon.</span>
              </p>

              <div className="mt-8 flex flex-wrap items-baseline gap-3">
                <div className="font-serif-display text-6xl text-white">included</div>
                <div className="text-sm uppercase tracking-wider font-bold" style={{ color: GOLD }}>
                  one-time · <span className="line-through opacity-60 text-white"> value</span>
                </div>
              </div>

              <button
                onClick={buy}
                disabled={loading}
                data-testid="starter-buy-hero"
                className="mt-6 inline-flex items-center gap-3 px-8 py-4 rounded-md font-bold uppercase tracking-[0.18em] text-sm shadow-2xl hover:-translate-y-0.5 transition disabled:opacity-60"
                style={{ background: ORANGE, color: NAVY }}
              >
                {loading ? "Opening Checkout…" : "★ Yes — I Want The Starter Guide"}
                <ArrowRight className="w-5 h-5" />
              </button>
              <div className="mt-3 text-xs tracking-wider-cap uppercase" style={{ color: "#9BA8C8" }}>
                Instant access · Drops into your dashboard · Stripe-secure checkout
              </div>
            </div>

            {/* Right column: the real Fast Start Guide bundle picture (same image used everywhere) */}
            <div className="relative flex items-center justify-center">
              <div className="absolute -inset-4 rounded-2xl blur-2xl opacity-30" style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 65%)` }}/>
              <img src="/free-guide/fast-start-bundle.png"
                   alt="Faitharama Fast Start Guide — Make $1,000 in 90 days · included"
                   loading="eager"
                   className="relative w-full max-w-md drop-shadow-2xl rounded-lg"
                   style={{ filter: "drop-shadow(0 18px 40px rgba(0,0,0,0.35))" }}/>
              <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.28em] uppercase font-bold px-3 py-1.5 rounded-full shadow-lg text-white"
                   style={{ background: "#2E9F3A" }}>
                included · One-Time
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── THE MATH ───────────── */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 py-14">
        <div className="text-[11px] tracking-[0.28em] uppercase font-bold" style={{ color: GOLD }}>★ The Math</div>
        <h2 className="font-serif-display text-3xl md:text-4xl text-red-wine uppercase mt-2">Why included Is A No-Brainer</h2>
        <div className="h-px w-16 mt-3" style={{ background: GOLD }} />

        <div className="mt-8 overflow-hidden rounded-xl border-2" style={{ borderColor: NAVY }}>
          <table className="w-full">
            <thead>
              <tr style={{ background: "#FFFFFF", color: "white" }}>
                <th className="text-left px-5 py-3 text-xs uppercase tracking-wider">What</th>
                <th className="text-left px-5 py-3 text-xs uppercase tracking-wider">Numbers</th>
                <th className="text-left px-5 py-3 text-xs uppercase tracking-wider">Result</th>
              </tr>
            </thead>
            <tbody className="text-base">
              <tr className="border-b border-foreground/10"><td className="px-5 py-3">Daily contacts captured</td><td className="px-5 py-3">3 to 5 / day</td><td className="px-5 py-3">—</td></tr>
              <tr className="bg-[#F4ECD8] border-b border-foreground/10"><td className="px-5 py-3">Monthly leads (30 days)</td><td className="px-5 py-3">3 × 30</td><td className="px-5 py-3 font-bold">90 leads</td></tr>
              <tr className="border-b border-foreground/10"><td className="px-5 py-3">Conversion rate (industry low)</td><td className="px-5 py-3">5%</td><td className="px-5 py-3 font-bold">4 to 5 sales</td></tr>
              <tr className="bg-[#F4ECD8] border-b border-foreground/10"><td className="px-5 py-3">Average first commission</td><td className="px-5 py-3"></td><td className="px-5 py-3">—</td></tr>
              <tr style={{ background: GOLD, color: NAVY }} className="font-bold"><td className="px-5 py-4 text-base">MONTH ONE TOTAL</td><td className="px-5 py-4">—</td><td className="px-5 py-4 text-lg"> – </td></tr>
              <tr className="border-t border-foreground/10"><td className="px-5 py-3">Month 2 (bigger list)</td><td className="px-5 py-3">—</td><td className="px-5 py-3 font-bold"></td></tr>
              <tr className="bg-[#F4ECD8]"><td className="px-5 py-3">Month 6 (compounding)</td><td className="px-5 py-3">—</td><td className="px-5 py-3 font-bold">real ministry income</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* ───────────── 7 VIDEOS ───────────── */}
      <section className="py-14" style={{ background: "#F4ECD8" }}>
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <div className="text-[11px] tracking-[0.28em] uppercase font-bold" style={{ color: RED }}>★ What's Inside</div>
          <h2 className="font-serif-display text-3xl md:text-4xl text-red-wine uppercase mt-2">7 Walk-Through Videos</h2>
          <div className="h-px w-16 mt-3" style={{ background: GOLD }} />
          <p className="text-base text-foreground mt-4 max-w-2xl">
            About 40 minutes of screen-share teaching with Mike. Each video maps to one chapter of the guide.
            Watch one a day, do the homework, and you'll cross your first  by Day 30.
          </p>

          <div className="grid md:grid-cols-2 gap-4 mt-8">
            {VIDEOS.map((v) => (
              <div key={v.num} className="rounded-xl bg-white border-2 overflow-hidden hover:-translate-y-0.5 transition shadow-sm hover:shadow-lg flex"
                   style={{ borderColor: "rgba(19,37,74,0.15)" }} data-testid={`starter-video-${v.num}`}>
                <div className="relative flex-shrink-0 w-32 sm:w-40"
                     style={{ background: "#FFFFFF" }}>
                  <img src={`/flipbook-page-0${((v.num - 1) % 8) + 1}.png`}
                       alt={`Video ${v.num} — ${v.title}`}
                       loading="lazy"
                       className="absolute inset-0 w-full h-full object-cover"/>
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 30%, rgba(15,26,46,0.78) 100%)" }}/>
                  <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[9px] tracking-[0.18em] uppercase font-bold"
                       style={{ background: GOLD, color: NAVY }}>
                    Video {v.num}
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="rounded-full flex items-center justify-center shadow-xl"
                         style={{ width: 44, height: 44, background: "rgba(201,162,39,0.92)" }}>
                      <Video className="w-5 h-5" style={{ color: NAVY }}/>
                    </div>
                  </div>
                </div>
                <div className="flex-1 p-4">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <div className="font-bold text-base text-red-wine">{v.title}</div>
                    <div className="text-[10px] uppercase tracking-wider" style={{ color: GOLD }}>{v.runtime}</div>
                  </div>
                  <p className="text-sm text-foreground/80 mt-1 leading-snug">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── PEEK INSIDE — real page thumbnails ───────────── */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-14">
        <div className="text-center">
          <div className="text-[11px] tracking-[0.28em] uppercase font-bold" style={{ color: GOLD }}>★ Peek Inside</div>
          <h2 className="font-serif-display text-3xl md:text-4xl text-red-wine uppercase mt-2">Real Pages From The Guide</h2>
          <div className="h-px w-16 mt-3 mx-auto" style={{ background: GOLD }} />
          <p className="text-base text-foreground/80 mt-4 max-w-2xl mx-auto">
            No fluff. Big print. Plain English. Here's what's waitin' inside.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 mt-8">
          {[1,2,3,4,5,6,7,8].map((n) => (
            <div key={n} className="rounded-lg overflow-hidden border-2 shadow hover:shadow-xl hover:-translate-y-0.5 transition"
                 style={{ borderColor: `${GOLD}55`, background: "#FFFFFF" }}>
              <img src={`/flipbook-page-0${n}.png`}
                   alt={`Page ${n} of the Faitharama Starter Guide`}
                   loading="lazy"
                   className="block w-full h-auto"/>
            </div>
          ))}
        </div>
      </section>

      {/* ───────────── BONUSES ───────────── */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 py-14">
        <div className="text-[11px] tracking-[0.28em] uppercase font-bold" style={{ color: GOLD }}>★ Included Free</div>
        <h2 className="font-serif-display text-3xl md:text-4xl text-red-wine uppercase mt-2">6 Bonuses</h2>
        <div className="h-px w-16 mt-3" style={{ background: GOLD }} />
        <p className="text-base text-foreground mt-4 max-w-2xl">
          Six PDFs bundled in. Each one's something you'd usually pay $7–included for on its own. <strong>Less than 8 bucks for the whole bundle</strong> — Mike's way of building trust before he asks for a bigger yes.
        </p>

        <div className="grid md:grid-cols-2 gap-3 mt-8">
          {BONUSES.map((b) => (
            <div key={b.num} className="rounded-lg bg-[#F4ECD8] border p-4 flex gap-3 items-start"
                 style={{ borderColor: "rgba(19,37,74,0.15)" }} data-testid={`starter-bonus-${b.num}`}>
              <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm"
                   style={{ background: GOLD, color: NAVY }}>
                {b.num}
              </div>
              <div className="flex-1">
                <div className="font-bold text-sm text-red-wine">{b.title}</div>
                <p className="text-sm text-foreground/80 mt-0.5">{b.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ───────────── WHAT'S DELIVERED ───────────── */}
      <section className="py-14" style={{ background: "#FFFFFF" }}>
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <div className="text-[11px] tracking-[0.28em] uppercase font-bold" style={{ color: GOLD }}>★ Instant Access</div>
          <h2 className="font-serif-display text-3xl md:text-4xl text-white uppercase mt-2">How You Get It</h2>
          <div className="h-px w-16 mt-3 mx-auto" style={{ background: GOLD }} />

          <div className="grid sm:grid-cols-3 gap-5 mt-10">
            {[
              { img: "/free-guide/fast-start-bundle.png", t: "24-Page PDF Guide", d: "Print it. Mark it up. Keep it on your desk." },
              { img: "/free-guide/fast-start-bundle.png", t: "7 Video Walk-Throughs", d: "Watch in your dashboard. Pause. Re-watch. Yours forever." },
              { img: "/free-guide/fast-start-bundle.png", t: "90-Day Roadmap", d: "Step-by-step plan: foundation → traffic → $1,000/mo." },
            ].map(({ img, t, d }, i) => (
              <div key={i} className="rounded-xl overflow-hidden border-2 bg-white"
                   style={{ borderColor: "#2E9F3A" }}>
                <div className="relative w-full overflow-hidden" style={{ aspectRatio: "4/3", background: "#EAF6FC" }}>
                  <img src={img} alt={t}
                       loading="lazy"
                       className="absolute inset-0 w-full h-full object-contain p-3"/>
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(15,26,46,0.85) 100%)" }}/>
                </div>
                <div className="p-5 text-center">
                  <div className="font-bold text-base text-white">{t}</div>
                  <p className="text-sm mt-1.5 leading-snug" style={{ color: "#C6CDDB" }}>{d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── FINAL CTA ───────────── */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 py-14 text-center">
        <Sparkles className="w-8 h-8 mx-auto mb-3" style={{ color: GOLD }} />
        <h2 className="font-serif-display text-3xl md:text-4xl text-red-wine uppercase">Raise The Hammer.</h2>
        <div className="h-px w-16 mt-3 mx-auto" style={{ background: GOLD }} />
        <p className="text-base md:text-lg text-foreground mt-5 leading-relaxed">
          God didn't NEED Noah to build the boat. <b>He chose Noah.</b><br/>
          Faitharama is the boat. This guide is the hammer.<br/>
          The work is yours.
        </p>

        <div className="mt-8 inline-block">
          <button
            onClick={buy}
            disabled={loading}
            data-testid="starter-buy-final"
            className="inline-flex items-center gap-3 px-9 py-4 rounded-md font-bold uppercase tracking-[0.18em] text-sm shadow-2xl hover:-translate-y-0.5 transition disabled:opacity-60"
            style={{ background: RED, color: "white" }}
          >
            {loading ? "Opening Checkout…" : "★ Get The Starter Guide · included"}
            <ArrowRight className="w-5 h-5" />
          </button>
          <div className="mt-3 text-xs tracking-wider-cap uppercase text-foreground/60 inline-flex items-center gap-1.5 justify-center">
            <Lock className="w-3 h-3" /> Secure checkout via Stripe · No subscription
          </div>
        </div>

        <div className="mt-10 text-xs text-foreground/50 max-w-xl mx-auto">
          One-time included charge. Instant delivery to your Faitharama dashboard under "My Courses."
          Need help? <Link to="/contact" className="underline text-red-wine">Email Mike directly</Link>.
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
